import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Modal } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import api from "../api";
import { exportDistrictData } from "./exportFile";
import { exportDistrictDatas } from "./exportConFile";

const Export = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString("default", { month: "long" });
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", { month: "long" }),
  );

  const [mode, setMode] = useState("static"); // "static" | "cumulative"
  const [stateList, setStateList] = useState([]);
  const [isStaticExporting, setIsStaticExporting] = useState(false);
  const [isCumExporting, setIsCumExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  // ── Static mode state ──────────────────────────────────────────────────────
  const [allStates, setAllStates] = useState(false);
  const [selectedStates, setSelectedStates] = useState([]);
  const [districtsByState, setDistrictsByState] = useState({});
  const [selectedDistricts, setSelectedDistricts] = useState({});
  const [loadingDistricts, setLoadingDistricts] = useState({});
  const [year, setYear] = useState(currentYear.toString());
  const [month, setMonth] = useState(currentMonth);

  // ── Cumulative mode state ──────────────────────────────────────────────────
  const [conFilters, setConFilters] = useState({
    Year: currentYear.toString(),
    ToYear: currentYear.toString(),
    Month: currentMonth,
    ToMonth: currentMonth,
  });
  const [missingDistricts, setMissingDistricts] = useState(null);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // ── Cumulative state/district selection ────────────────────────────────────
  const [conAllStates, setConAllStates] = useState(false);
  const [conSelectedStates, setConSelectedStates] = useState([]);
  const [conSelectedDistricts, setConSelectedDistricts] = useState({});

  // ── Load states ────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await api.get("/states");
        setStateList(res.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        toast.error("Failed to load states list");
      }
    };
    fetchStates();
  }, []);

  // ── Progress bar ───────────────────────────────────────────────────────────
  const isExporting = isStaticExporting || isCumExporting;

  useEffect(() => {
    let timer;
    if (isExporting) {
      setProgress(10);
      timer = setInterval(() => {
        setProgress((prev) => (prev >= 90 ? 90 : prev + 5));
      }, 300);
    } else {
      setProgress(100);
      const resetTimer = setTimeout(() => setProgress(0), 500);
      return () => clearTimeout(resetTimer);
    }
    return () => clearInterval(timer);
  }, [isExporting]);

  // ── Static helpers ─────────────────────────────────────────────────────────
  const fetchDistricts = async (stateId, stateName) => {
    if (districtsByState[stateName]) return;
    setLoadingDistricts((prev) => ({ ...prev, [stateName]: true }));
    try {
      const res = await api.get(`/cities/${stateId}`);
      const options = res.data.map((d) => ({ value: d.city, label: d.city }));
      setDistrictsByState((prev) => ({ ...prev, [stateName]: options }));
    } catch (err) {
      toast.error(`Failed to load districts for ${stateName}`);
    } finally {
      setLoadingDistricts((prev) => ({ ...prev, [stateName]: false }));
    }
  };

  const handleStateChange = (selected) => {
    setSelectedStates(selected || []);
    const selectedNames = new Set((selected || []).map((s) => s.value));
    setSelectedDistricts((prev) => {
      const updated = {};
      for (const name of Object.keys(prev)) {
        if (selectedNames.has(name)) updated[name] = prev[name];
      }
      return updated;
    });
    (selected || []).forEach((s) => fetchDistricts(s.id, s.value));
  };

  const handleAllStatesToggle = (e) => {
    setAllStates(e.target.checked);
    if (e.target.checked) {
      setSelectedStates([]);
      setSelectedDistricts({});
    }
  };

  const handleDistrictChange = (stateName, selected) => {
    setSelectedDistricts((prev) => ({ ...prev, [stateName]: selected || [] }));
  };

  const buildStaticRequestBody = () => {
    // let stateField;
    let stateField = resolveStates(allStates, selectedStates);
    // if (allStates) {
    //   stateField = "ALL_STATE";
    // } else if (selectedStates.length === 1) {
    //   stateField = selectedStates[0].value;
    // } else {
    //   stateField = selectedStates.map((s) => s.value);
    // }

    const body = { State: stateField, Year: year, Month: month };

    const districtsMap = {};
    for (const [stateName, districts] of Object.entries(selectedDistricts)) {
      if (districts && districts.length > 0) {
        districtsMap[stateName] = districts.map((d) => d.value);
      }
    }
    if (Object.keys(districtsMap).length > 0) {
      body.Districts = districtsMap;
    }

    return body;
  };

  const handleStaticExport = async (e) => {
    e.preventDefault();
    if (!allStates && selectedStates.length === 0) {
      toast.warning("Please select at least one state or choose All States");
      return;
    }
    const requestBody = buildStaticRequestBody();
    setIsStaticExporting(true);
    try {
      await exportDistrictData({
        Year: requestBody.Year,
        Month: requestBody.Month,
        State: requestBody.State,
        Districts: requestBody.Districts,
      });
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setIsStaticExporting(false);
    }
  };

  const handleStaticReset = () => {
    setAllStates(false);
    setSelectedStates([]);
    setSelectedDistricts({});
    setYear(currentYear.toString());
    setMonth(currentMonth);
  };

  // ── Cumulative helpers ─────────────────────────────────────────────────────
  const handleConAllStatesToggle = (e) => {
    setConAllStates(e.target.checked);
    if (e.target.checked) {
      setConSelectedStates([]);
      setConSelectedDistricts({});
    }
  };

  const handleConStateChange = (selected) => {
    setConSelectedStates(selected || []);
    const selectedNames = new Set((selected || []).map((s) => s.value));
    setConSelectedDistricts((prev) => {
      const updated = {};
      for (const name of Object.keys(prev)) {
        if (selectedNames.has(name)) updated[name] = prev[name];
      }
      return updated;
    });
    (selected || []).forEach((s) => fetchDistricts(s.id, s.value));
  };

  const handleConDistrictChange = (stateName, selected) => {
    setConSelectedDistricts((prev) => ({
      ...prev,
      [stateName]: selected || [],
    }));
  };

  const handleConChange = (e) => {
    const { name, value } = e.target;
    const temp = { ...conFilters, [name]: value };

    const Year = parseInt(temp.Year);
    const ToYear = parseInt(temp.ToYear);
    const Month = temp.Month;
    const ToMonth = temp.ToMonth;
    const monthIndex = (m) => new Date(Date.parse(m + " 1, 2000")).getMonth();

    // ---- YEAR VALIDATION ----
    if (name === "Year" || name === "ToYear") {
      if (Year && ToYear && Year > ToYear) {
        toast.error("Start Year cannot be greater than End Year");
        return;
      }
    }

    if (Year === ToYear) {
      if (Month && ToMonth) {
        if (monthIndex(Month) > monthIndex(ToMonth)) {
          toast.error("From Month cannot be greater than To Month");
          return;
        }
      }
    }

    // ---- MONTH VALIDATION (ONLY IF SAME YEAR) ----
    if (Year && ToYear && Year === ToYear) {
      if (name === "Month" || name === "ToMonth") {
        if (Month && ToMonth) {
          if (monthIndex(Month) > monthIndex(ToMonth)) {
            toast.error("Start Month cannot be greater than End Month");
            return;
          }
        }
      }
    }

    setConFilters(temp);
  };

  const handleCumulativeExport = async (e) => {
    e.preventDefault();
    const { Year, ToYear, Month, ToMonth } = conFilters;

    if (!conAllStates && conSelectedStates.length === 0) {
      toast.warning("Please select at least one state or choose All States");
      return;
    }

    setIsCumExporting(true);

    try {
      let stateField = resolveStates(conAllStates, conSelectedStates);
      // if (conAllStates) {
      //   stateField = "ALL_STATE";
      // } else if (conSelectedStates.length === 1) {
      //   stateField = conSelectedStates[0].value;
      // } else {
      //   stateField = conSelectedStates.map((s) => s.value);
      // }

      const districtsMap = {};
      for (const [stateName, districts] of Object.entries(
        conSelectedDistricts,
      )) {
        if (districts && districts.length > 0) {
          districtsMap[stateName] = districts.map((d) => d.value);
        }
      }

      const success = await exportDistrictDatas({
        StartYear: Year,
        EndYear: ToYear,
        StartMonth: Month,
        EndMonth: ToMonth,
        State: stateField,
        Districts:
          Object.keys(districtsMap).length > 0 ? districtsMap : undefined,
        setMissingDistricts,
        setShowMissingModal,
        setErrorMessage,
        setShowErrorModal,
      });
      if (success) {
        setConFilters({
          Year: currentYear.toString(),
          ToYear: currentYear.toString(),
          Month: currentMonth,
          ToMonth: currentMonth,
        });
        setConAllStates(false);
        setConSelectedStates([]);
        setConSelectedDistricts({});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCumExporting(false);
    }
  };

  const handleCumulativeReset = () => {
    setConFilters({
      Year: currentYear.toString(),
      ToYear: currentYear.toString(),
      Month: currentMonth,
      ToMonth: currentMonth,
    });
    setConAllStates(false);
    setConSelectedStates([]);
    setConSelectedDistricts({});
  };

  const getUserAssignedStates = () => {
    const currentUser = JSON.parse(localStorage.getItem("user"));

    let assignedStates = currentUser?.assigned_state;

    if (typeof assignedStates === "string") {
      try {
        assignedStates = JSON.parse(assignedStates);
      } catch {
        assignedStates = [];
      }
    }

    const allStatesAccess =
      !Array.isArray(assignedStates) || assignedStates.length === 0;

    return { assignedStates, allStatesAccess };
  };

  const { assignedStates, allStatesAccess } = getUserAssignedStates();

  const assignedStateSet = new Set(
    (assignedStates || []).map((s) => s?.toString().trim().toLowerCase()),
  );

  const resolveStates = (useAllStates, selectedStates) => {
    // CASE 1: user clicked "All States"
    if (useAllStates) {
      // unrestricted user → send ALL_STATE
      if (allStatesAccess) {
        return "ALL_STATE";
      }

      // restricted user → send only assigned states
      return assignedStates;
    }

    // CASE 2: manual selection
    if (selectedStates.length === 1) {
      return selectedStates[0].value;
    }

    return selectedStates.map((s) => s.value);
  };
  const stateOptions = stateList
    .filter((s) =>
      allStatesAccess
        ? true
        : assignedStateSet.has(s.state?.toString().trim().toLowerCase()),
    )
    .map((s) => ({
      value: s.state,
      label: s.state,
      id: s.id,
    }));

  // ── Shared ─────────────────────────────────────────────────────────────────
  // const stateOptions = stateList.map((s) => ({
  //   value: s.state,
  //   label: s.state,
  //   id: s.id,
  // }));

  const showAllStatesCheckbox =
    !Array.isArray(assignedStates) || assignedStates.length === 0;

  return (
    <>
      <div className="card hptCard mt-4 border-0">
        <div className="card-body">
          {/* Mode toggle */}
          <div className="d-flex gap-2 mb-4">
            <Button
              variant={mode === "static" ? "success" : "outline-success"}
              onClick={() => setMode("static")}
            >
              <i className="bi bi-file-earmark-spreadsheet me-2"></i>
              Static Export
            </Button>
            <Button
              variant={mode === "cumulative" ? "primary" : "outline-primary"}
              onClick={() => setMode("cumulative")}
            >
              <i className="bi bi-layers me-2"></i>
              Cumulative Export
            </Button>
          </div>

          {/* ── Static form ── */}
          {mode === "static" && (
            <Form onSubmit={handleStaticExport}>
              <Row className="mb-3 g-2 align-items-end">
                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar me-2"></i>Year *
                    </Form.Label>
                    <Form.Select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      required
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar-month me-2"></i>Month *
                    </Form.Label>
                    <Form.Select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      required
                    >
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4} className="export-col">
                  <Form.Group>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <Form.Label className="mb-0">
                        <i className="bi bi-geo-alt me-2"></i>State(s) *
                      </Form.Label>
                      {/* {showAllStatesCheckbox && ( */}
                        <Form.Check
                          type="checkbox"
                          id="allStatesCheck"
                          label="All States"
                          checked={allStates}
                          onChange={handleAllStatesToggle}
                          className="mb-0"
                        />
                      {/* )} */}
                    </div>
                    {!allStates && (
                      <Select
                        isMulti
                        options={stateOptions}
                        value={selectedStates}
                        onChange={handleStateChange}
                        placeholder="Select state(s)..."
                        classNamePrefix="react-select"
                      />
                    )}
                    {allStates && (
                      <div
                        className="form-control bg-light text-muted"
                        style={{ cursor: "default" }}
                      >
                        All states selected
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {!allStates && selectedStates.length > 0 && (
                <Row className="mb-3 g-2">
                  <Col md={12}>
                    <Form.Label>
                      <i className="bi bi-building me-2"></i>Districts{" "}
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.85em", fontWeight: "normal" }}
                      >
                        (optional — leave blank to export all districts)
                      </span>
                    </Form.Label>
                    {selectedStates.map((s) => (
                      <div key={s.value} className="mb-2">
                        <Form.Label
                          style={{ fontSize: "0.88em", fontWeight: "600" }}
                        >
                          {s.value}
                        </Form.Label>
                        <Select
                          isMulti
                          options={districtsByState[s.value] || []}
                          value={selectedDistricts[s.value] || []}
                          onChange={(sel) => handleDistrictChange(s.value, sel)}
                          placeholder={
                            loadingDistricts[s.value]
                              ? "Loading districts..."
                              : "All districts (or select specific ones)"
                          }
                          isLoading={loadingDistricts[s.value]}
                          classNamePrefix="react-select"
                        />
                      </div>
                    ))}
                  </Col>
                </Row>
              )}

              <div className="d-flex gap-2 mt-2">
                <Button
                  type="submit"
                  variant="success"
                  disabled={isStaticExporting}
                >
                  {isStaticExporting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Export to Excel
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleStaticReset}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>Reset
                </Button>
              </div>
            </Form>
          )}

          {/* ── Cumulative form ── */}
          {mode === "cumulative" && (
            <Form onSubmit={handleCumulativeExport}>
              <Row className="mb-3 g-2 align-items-end">
                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar me-2"></i>Start Year *
                    </Form.Label>
                    <Form.Select
                      name="Year"
                      value={conFilters.Year}
                      onChange={handleConChange}
                      required
                    >
                      <option value="">Start Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar me-2"></i>End Year *
                    </Form.Label>
                    <Form.Select
                      name="ToYear"
                      value={conFilters.ToYear}
                      onChange={handleConChange}
                      required
                    >
                      <option value="">End Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar-month me-2"></i>Start Month
                    </Form.Label>
                    <Form.Select
                      name="Month"
                      value={conFilters.Month}
                      onChange={handleConChange}
                    >
                      <option value="">Start Month</option>
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2} className="export-col">
                  <Form.Group>
                    <Form.Label>
                      <i className="bi bi-calendar-month me-2"></i>End Month
                    </Form.Label>
                    <Form.Select
                      name="ToMonth"
                      value={conFilters.ToMonth}
                      onChange={handleConChange}
                    >
                      <option value="">End Month</option>
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4} className="export-col">
                  <Form.Group>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <Form.Label className="mb-0">
                        <i className="bi bi-geo-alt me-2"></i>State(s) *
                      </Form.Label>
                      {/* <Form.Check
                        type="checkbox"
                        id="conAllStatesCheck"
                        label="All States"
                        checked={conAllStates}
                        onChange={handleConAllStatesToggle}
                        className="mb-0"
                      /> */}
                    </div>
                    {!conAllStates && (
                      <Select
                        isMulti
                        options={stateOptions}
                        value={conSelectedStates}
                        onChange={handleConStateChange}
                        placeholder="Select state(s)..."
                        classNamePrefix="react-select"
                      />
                    )}
                    {conAllStates && (
                      <div
                        className="form-control bg-light text-muted"
                        style={{ cursor: "default" }}
                      >
                        All states selected
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {!conAllStates && conSelectedStates.length > 0 && (
                <Row className="mb-3 g-2">
                  <Col md={12}>
                    <Form.Label>
                      <i className="bi bi-building me-2"></i>Districts{" "}
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.85em", fontWeight: "normal" }}
                      >
                        (optional — leave blank to export all districts)
                      </span>
                    </Form.Label>
                    {conSelectedStates.map((s) => (
                      <div key={s.value} className="mb-2">
                        <Form.Label
                          style={{ fontSize: "0.88em", fontWeight: "600" }}
                        >
                          {s.value}
                        </Form.Label>
                        <Select
                          isMulti
                          options={districtsByState[s.value] || []}
                          value={conSelectedDistricts[s.value] || []}
                          onChange={(sel) =>
                            handleConDistrictChange(s.value, sel)
                          }
                          placeholder={
                            loadingDistricts[s.value]
                              ? "Loading districts..."
                              : "All districts (or select specific ones)"
                          }
                          isLoading={loadingDistricts[s.value]}
                          classNamePrefix="react-select"
                        />
                      </div>
                    ))}
                  </Col>
                </Row>
              )}

              <div className="d-flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isCumExporting}
                >
                  {isCumExporting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Export to Excel
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleCumulativeReset}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>Reset
                </Button>
              </div>
            </Form>
          )}

          {/* Progress indicator */}
          {isExporting && (
            <div className="mt-4 p-3 border rounded bg-light">
              <div className="d-flex align-items-center gap-3">
                <div className="spinner-border" role="status"></div>
                <div>
                  <strong>Exporting...</strong>
                  <div className="text-muted" style={{ fontSize: "14px" }}>
                    Please wait while your Excel file is being generated.
                  </div>
                </div>
              </div>
              <div className="progress mt-3" style={{ height: "6px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: progress + "%" }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Missing districts modal (cumulative) */}
      <Modal
        show={showMissingModal}
        onHide={() => setShowMissingModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Missing District Data</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
          {missingDistricts &&
            Object.entries(missingDistricts).map(([stateName, entries]) => (
              <div key={stateName} className="mb-4">
                <h6 className="text-primary border-bottom pb-1">{stateName}</h6>
                {entries.map(({ district, missingMonths }) => (
                  <div key={district} className="mb-2 ms-2">
                    <strong>{district}</strong>
                    <ul className="mb-0">
                      {missingMonths.map((m) => (
                        <li key={m} className="text-muted">
                          {m}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMissingModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error modal */}
      <Modal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
          setErrorMessage(null);
        }}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="text-danger" style={{ fontSize: "1rem" }}>
            <i className="bi bi-exclamation-circle me-2"></i>Export Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-0">{errorMessage}</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="danger"
            onClick={() => {
              setShowErrorModal(false);
              setErrorMessage(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Export;
